import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import Autocomplete, {
  AutocompleteProps as MuiAutocompleteProps,
  AutocompleteInputChangeReason,
  AutocompleteRenderInputParams,
} from "@mui/material/Autocomplete";
import axios from "../../../api/axios";
import { useTranslation } from "react-i18next";

export type OptionRecord = { id: string | number } & Record<string, any>;

interface CustomAutocompleteApiConfig {
  url: string;
  queryParams?: Record<string, string | number | null | undefined>;
  minSearchChars?: number;
  debounceMs?: number;
  searchParamName?: string;
}

type BaseAutocompleteProps<
  TOption extends OptionRecord,
  Multiple extends boolean | undefined,
  DisableClearable extends boolean | undefined,
  FreeSolo extends boolean | undefined
> = Omit<
  MuiAutocompleteProps<TOption, Multiple, DisableClearable, FreeSolo>,
  "options" | "loading" | "renderInput"
> & {
  options?: TOption[];
  loading?: boolean;
  apiConfig?: CustomAutocompleteApiConfig;
  fetchTrigger?: unknown;
  onOptionsChange?: (options: TOption[]) => void;
  onLoadingChange?: (loading: boolean) => void;
  renderInput: (params: AutocompleteRenderInputParams) => React.ReactNode;
  onInputChange?: (
    event: React.SyntheticEvent,
    value: string,
    reason: AutocompleteInputChangeReason
  ) => void;
  /**
   * Field name to use as the unique identifier for options.
   * Defaults to "id"
   */
  idField?: string;
  /**
   * Field name(s) to use for displaying option labels.
   * If an array, will concatenate field values.
   * Defaults to "name"
   */
  labelField?: string | string[];
  /**
   * Extract IDs from selected value(s).
   * If not provided, assumes value has an `id` property or is an ID itself.
   */
  getValueIds?: (value: unknown) => (string | number)[];
  /**
   * Rebuild value object(s) from ID(s) using cached options.
   * Useful for form submission where you need full objects, not just IDs.
   */
  buildValuesFromIds?: (
    ids: (string | number)[],
    cachedOptions: TOption[]
  ) => TOption[];
  /**
   * Enable automatic merging of fetched options with existing options.
   * Prevents duplicates based on the idField.
   */
  mergeOptions?: boolean;
};

export default function CustomAutocomplete<
  TOption extends OptionRecord,
  Multiple extends boolean | undefined = false,
  DisableClearable extends boolean | undefined = false,
  FreeSolo extends boolean | undefined = false
>({
  options,
  loading,
  apiConfig,
  fetchTrigger,
  onOptionsChange,
  onLoadingChange,
  onOpen,
  onInputChange,
  slotProps,
  idField = "id",
  labelField = "name",
  getValueIds,
  buildValuesFromIds,
  mergeOptions = false,
  ...props
}: BaseAutocompleteProps<TOption, Multiple, DisableClearable, FreeSolo>) {
  const { t } = useTranslation();
  const [internalOptions, setInternalOptions] = useState<TOption[]>([]);
  const [internalLoading, setInternalLoading] = useState(false);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const requestIdRef = useRef(0);
  const lastSearchRef = useRef("");
  const nextPageUrlRef = useRef<string | null>(null);
  const isFetchingNextPageRef = useRef(false);
  const listboxRef = useRef<HTMLUListElement | null>(null);
  const lastListboxScrollTopRef = useRef(0);
  const restoreScrollTopRef = useRef<number | null>(null);
  const restoreAnimationFramesRef = useRef<number[]>([]);
  const restoreTimeoutsRef = useRef<number[]>([]);

  const usingApi = Boolean(apiConfig);
  const rawListboxSlotProps = slotProps?.listbox;
  const listboxSlotProps = useMemo(
    () => (rawListboxSlotProps ?? {}) as Record<string, any>,
    [rawListboxSlotProps]
  );

  const setLoadingState = useCallback(
    (nextLoading: boolean) => {
      setInternalLoading(nextLoading);
      onLoadingChange?.(nextLoading);
    },
    [onLoadingChange]
  );

  /**
   * Merge new fetched options with existing options to prevent duplicates
   */
  const mergeOptionsList = useCallback(
    (
      newOptions: TOption[],
      existingOptions: TOption[],
      forceMerge = false
    ): TOption[] => {
      if (!mergeOptions && !forceMerge) return newOptions;

      const merged = [...existingOptions];
      newOptions.forEach((newOption) => {
        const newId = newOption[idField];
        if (!merged.find((opt) => opt[idField] === newId)) {
          merged.push(newOption);
        }
      });
      return merged;
    },
    [idField, mergeOptions]
  );

  const clearScheduledScrollRestore = useCallback(() => {
    restoreAnimationFramesRef.current.forEach((frame) => window.cancelAnimationFrame(frame));
    restoreAnimationFramesRef.current = [];

    restoreTimeoutsRef.current.forEach((timeoutId) => window.clearTimeout(timeoutId));
    restoreTimeoutsRef.current = [];
  }, []);

  const scheduleScrollRestore = useCallback((scrollTop: number) => {
    const target = listboxRef.current;
    if (!target) {
      return;
    }

    const applyScrollTop = () => {
      if (!listboxRef.current) {
        return;
      }

      listboxRef.current.scrollTop = scrollTop;
      lastListboxScrollTopRef.current = scrollTop;
    };

    clearScheduledScrollRestore();
    applyScrollTop();

    const frameIds: number[] = [];
    const timeoutIds: number[] = [];

    const queueAnimationFrameRestore = (remainingRuns: number) => {
      if (remainingRuns <= 0) {
        return;
      }

      const frameId = window.requestAnimationFrame(() => {
        applyScrollTop();
        queueAnimationFrameRestore(remainingRuns - 1);
      });

      frameIds.push(frameId);
      restoreAnimationFramesRef.current = [...frameIds];
    };

    queueAnimationFrameRestore(6);

    [0, 40, 120].forEach((delay) => {
      const timeoutId = window.setTimeout(() => {
        applyScrollTop();

        if (delay === 120) {
          restoreScrollTopRef.current = null;
        }
      }, delay);

      timeoutIds.push(timeoutId);
      restoreTimeoutsRef.current = [...timeoutIds];
    });
  }, [clearScheduledScrollRestore]);

  const resolveOptionKey = useCallback(
    (option: TOption | string) => {
      if (typeof option === "string") {
        return option;
      }

      const optionId = option?.[idField];
      if (optionId !== undefined && optionId !== null && optionId !== "") {
        return optionId;
      }

      if (Array.isArray(labelField)) {
        const labelValue = labelField
          .map((fieldName) => option?.[fieldName])
          .filter((value) => value !== undefined && value !== null && value !== "")
          .join("-");

        if (labelValue) {
          return labelValue;
        }
      }

      const singleLabelValue = typeof labelField === "string" ? option?.[labelField] : undefined;
      if (singleLabelValue !== undefined && singleLabelValue !== null && singleLabelValue !== "") {
        return String(singleLabelValue);
      }

      return JSON.stringify(option);
    },
    [idField, labelField]
  );

  const assignListboxRef = useCallback(
    (node: HTMLUListElement | null) => {
      listboxRef.current = node;

      if (node) {
        const targetScrollTop = restoreScrollTopRef.current ?? lastListboxScrollTopRef.current;
        if (targetScrollTop > 0) {
          scheduleScrollRestore(targetScrollTop);
        }
      }

      const externalRef = listboxSlotProps.ref;
      if (typeof externalRef === "function") {
        externalRef(node);
      } else if (externalRef && typeof externalRef === "object") {
        externalRef.current = node;
      }
    },
    [listboxSlotProps, scheduleScrollRestore]
  );

  const runFetch = useCallback(
    async ({
      searchValue = "",
      pageUrl,
      append = false,
    }: {
      searchValue?: string;
      pageUrl?: string | null;
      append?: boolean;
    } = {}) => {
      if (!apiConfig) {
        return;
      }

      const minChars = apiConfig.minSearchChars ?? 2;
      const trimmedSearchValue = searchValue.trim();

      if (!pageUrl && trimmedSearchValue.length > 0 && trimmedSearchValue.length < minChars) {
        return;
      }

      if (append && (!pageUrl || isFetchingNextPageRef.current)) {
        return;
      }

      const currentRequestId = requestIdRef.current + 1;
      requestIdRef.current = currentRequestId;

      if (append) {
        isFetchingNextPageRef.current = true;
        restoreScrollTopRef.current = lastListboxScrollTopRef.current;
      } else {
        nextPageUrlRef.current = null;
        restoreScrollTopRef.current = null;
      }

      setLoadingState(true);
      try {
        let requestUrl = pageUrl ?? null;

        if (!requestUrl) {
          const params = new URLSearchParams();

          if (apiConfig.queryParams) {
            Object.entries(apiConfig.queryParams).forEach(([key, value]) => {
              if (value === null || value === undefined || value === "") return;
              params.set(key, String(value));
            });
          }

          if (trimmedSearchValue) {
            params.set(apiConfig.searchParamName || "search", trimmedSearchValue);
          }

          const query = params.toString();
          requestUrl = `${apiConfig.url}${query ? `?${query}` : ""}`;
        }

        const { data } = await axios.get(requestUrl);

        if (requestIdRef.current !== currentRequestId) {
          return;
        }

        nextPageUrlRef.current = typeof data?.next === "string" ? data.next : null;

        const nextOptions = Array.isArray(data?.results)
            ? data.results
            : Array.isArray(data)
              ? data
              : [];

        const safeOptions = Array.isArray(nextOptions) ? nextOptions : [];
        let finalOptions: TOption[] = safeOptions;
        setInternalOptions((currentOptions) => {
          finalOptions = mergeOptionsList(safeOptions, currentOptions, append);
          return finalOptions;
        });
        onOptionsChange?.(finalOptions);
      } catch (error) {
        if (requestIdRef.current === currentRequestId) {
          nextPageUrlRef.current = null;
          if (!append) {
            setInternalOptions([]);
            onOptionsChange?.([]);
          }
        }
        console.error("Failed to fetch autocomplete options", error);
      } finally {
        if (append) {
          isFetchingNextPageRef.current = false;
        }
        if (requestIdRef.current === currentRequestId) {
          setLoadingState(false);
        }
      }
    },
    [apiConfig, onOptionsChange, setLoadingState, mergeOptionsList]
  );

  const loadNextPage = useCallback(() => {
    if (!usingApi || internalLoading) {
      return;
    }

    runFetch({
      searchValue: lastSearchRef.current,
      pageUrl: nextPageUrlRef.current,
      append: true,
    });
  }, [internalLoading, runFetch, usingApi]);

  const scheduleFetch = useCallback(
    (searchValue = "") => {
      if (!usingApi) return;
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }

      lastSearchRef.current = searchValue;

      const delay = apiConfig?.debounceMs ?? 300;
      debounceRef.current = setTimeout(() => {
        runFetch({ searchValue });
      }, delay);
    },
    [apiConfig?.debounceMs, runFetch, usingApi]
  );

  const handleListboxScroll = useCallback(
    (event: React.SyntheticEvent) => {
      listboxRef.current = event.currentTarget as HTMLUListElement;
      lastListboxScrollTopRef.current = listboxRef.current.scrollTop;
      listboxSlotProps.onScroll?.(event);

      const target = event.currentTarget as HTMLUListElement;
      const scrollThreshold = 24;
      const hasReachedBottom =
        target.scrollTop + target.clientHeight >= target.scrollHeight - scrollThreshold;

      if (hasReachedBottom) {
        loadNextPage();
      }
    },
    [listboxSlotProps, loadNextPage]
  );

  useEffect(() => {
    if (restoreScrollTopRef.current === null || !listboxRef.current) {
      return;
    }

    const scrollTop = restoreScrollTopRef.current;
    scheduleScrollRestore(scrollTop);

    return () => {
      clearScheduledScrollRestore();
    };
  }, [clearScheduledScrollRestore, internalLoading, internalOptions, scheduleScrollRestore]);

  useEffect(() => {
    return () => {
      clearScheduledScrollRestore();

      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    };
  }, [clearScheduledScrollRestore]);

  const resolvedOptions = useMemo(() => {
    if (usingApi) return internalOptions;
    return options || [];
  }, [usingApi, internalOptions, options]);

  const resolvedLoading = usingApi ? internalLoading : Boolean(loading);

  // The server already filtered `resolvedOptions` against the search term (potentially
  // matching fields not shown in the option label, e.g. an equipment's installation
  // address). MUI's default filterOptions re-filters options client-side against
  // getOptionLabel, which would hide correctly-returned results whose match isn't
  // reflected in the label. Skip that re-filtering in API mode unless the caller
  // supplies its own filterOptions.
  const passThroughFilterOptions = useCallback(
    (opts: TOption[]) => opts,
    []
  );

  return (
    <Autocomplete
      {...props}
      options={resolvedOptions}
      loading={resolvedLoading}
      filterOptions={props.filterOptions ?? (usingApi ? passThroughFilterOptions : undefined)}
      getOptionKey={props.getOptionKey ?? resolveOptionKey}
      slotProps={{
        ...slotProps,
        listbox: {
          ...listboxSlotProps,
          ref: assignListboxRef,
          onScroll: handleListboxScroll,
        },
      }}
      onOpen={(event) => {
        runFetch({ searchValue: "" });
        onOpen?.(event);
      }}
      onInputChange={(event, value, reason) => {
        onInputChange?.(event, value, reason);
        if (usingApi && reason === "input") {
          scheduleFetch(value);
        }
      }}
      renderInput={props.renderInput}
      loadingText={t("Loading...")}
      noOptionsText={t("No options")}
    />
  );
}
